import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { IconsModule } from '@progress/kendo-angular-icons';
import { GroupResult } from '@progress/kendo-data-query';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { CreateDrawingLogRequest, DrawingNumberResult, PnaPart } from '../../models/file-vault.models';

@Component({
  selector: 'fv-drawing-number-request',
  standalone: true,
  template: `
    <div class="drawing-request">
      <div class="header">
        <h3>Drawing Number Request</h3>
        <p class="subtitle">Select a PNA part and update the description placeholders.</p>
      </div>

      <form (ngSubmit)="submit()" #requestForm="ngForm">
        <div class="form-row">

          <div class="form-group">
            <label for="pnaPart">PNA Part</label>
            <kendo-dropdownlist
              id="pnaPart"
              [(ngModel)]="selectedPnaPartId"
              name="pnaPart"
              [data]="groupedPnaParts"
              textField="name"
              valueField="id"
              [valuePrimitive]="true"
              (valueChange)="onPnaPartChange($event)">
              <ng-template kendoDropDownListItemTemplate let-dataItem>
                 {{ dataItem?.name }} <span *ngIf="dataItem?.prefix"> - <i>{{ dataItem.prefix }}</i></span>
              </ng-template>
              <ng-template kendoDropDownListValueTemplate let-dataItem>
                {{ dataItem?.name || 'Select PNA Part' }} <span *ngIf="dataItem?.prefix"> - <i>{{ dataItem.prefix }}</i></span>
              </ng-template>
              <ng-template kendoDropDownListGroupTemplate let-groupName>
                        <strong style="font-weight: 900; color: #f54c4cff">{{ groupName }}</strong>
                    </ng-template>
            </kendo-dropdownlist>
          </div>
          <div class="form-group">
            <label>Show All PNA Parts</label>
            <kendo-switch [(ngModel)]="showAllPnaParts" name="showAllPnaParts" (ngModelChange)="onFilterChanged()"></kendo-switch>
          </div>
        </div>

        <div class="form-group">
          <label for="description">Description</label>
          <textarea kendoTextArea
                    id="description"
                    name="description"
                    [(ngModel)]="description"
                    [rows]="4"
                    placeholder="Description with #Variables# to update">
          </textarea>
          <div class="hint">Text wrapped in #...# will be highlighted below for manual updates.</div>
        </div>

        <div class="preview" *ngIf="description.trim().length > 0">
          <div class="preview-title">Highlight Preview</div>
          <div class="preview-body" [innerHTML]="getHighlightedDescription()"></div>
        </div>

        <div class="form-actions">
          <button kendoButton type="submit" [themeColor]="'primary'" [disabled]="!canSubmit || isSubmitting">
            {{ isSubmitting ? 'Submitting...' : 'Create Drawing Number' }}
          </button>
        </div>
      </form>

      <div class="result" *ngIf="lastResult">
        <div class="result-title">Created Drawing Number</div>
        <div class="result-number">{{ lastResult.drawingNumber }}</div>
        <div class="result-meta">
          <span *ngIf="lastResult.rtaNum">RTA: {{ lastResult.rtaNum }}</span>
          <span *ngIf="lastResult.prefix">Prefix: {{ lastResult.prefix }}</span>
          <span *ngIf="lastResult.documentLinked">✓ Document linked</span>
        </div>
      </div>

      <div class="warning" *ngIf="warningMessage">
        <kendo-icon name="warning"></kendo-icon>
        <span>{{ warningMessage }}</span>
      </div>

      <div class="error" *ngIf="errorMessage">{{ errorMessage }}</div>
    </div>
  `,
  styles: [`
    .drawing-request {
      max-width: 720px;
      margin: 0 auto;
      padding: 20px;
    }

    .header h3 {
      margin: 0 0 6px 0;
      font-size: 22px;
    }

    .subtitle {
      margin: 0 0 16px 0;
      color: var(--fv-text-secondary);
      font-size: 13px;
    }

    .form-row {
      display: grid;
      grid-template-columns: repeat(2, minmax(200px, 1fr));
      gap: 16px;
    }

    .form-group {
      margin-bottom: 16px;
    }

    .form-group label {
      display: block;
      margin-bottom: 6px;
      font-size: 13px;
      font-weight: 500;
    }

    .hint {
      font-size: 12px;
      color: var(--fv-text-muted);
      margin-top: 6px;
    }

    .preview {
      padding: 12px;
      margin-bottom: 16px;
    }

    .preview-title {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--fv-text-secondary);
      margin-bottom: 8px;
    }

    .preview-body {
      font-size: 13px;
      color: var(--fv-text);
      white-space: pre-wrap;
      word-break: break-word;
    }

    .token {
      color: #d97706;
      font-weight: 600;
    }

    .form-actions {
      display: flex;
      justify-content: flex-end;
      margin-top: 8px;
    }

    .result {
      margin-top: 20px;
      padding: 14px;
      background: var(--fv-status-success-bg);
      border: 1px solid var(--fv-status-success-border);
      border-radius: 8px;
    }

    .result-title {
      font-size: 12px;
      color: var(--fv-status-success-fg);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 6px;
    }

    .result-number {
      font-size: 20px;
      font-weight: 700;
      color: var(--fv-status-success-fg);
      margin-bottom: 4px;
    }

    .result-meta {
      display: flex;
      gap: 12px;
      font-size: 12px;
      color: var(--fv-status-success-fg);
    }

    .error {
      margin-top: 12px;
      color: var(--fv-status-error-fg);
      font-size: 13px;
    }

    .warning {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 12px;
      padding: 10px 14px;
      background: var(--fv-status-warning-bg, #fff8e1);
      border: 1px solid var(--fv-status-warning-border, #ffe082);
      border-radius: 6px;
      color: var(--fv-status-warning-fg, #b28704);
      font-size: 13px;
    }

    @media (max-width: 720px) {
      .form-row {
        grid-template-columns: 1fr;
      }
    }
  `],
  imports: [CommonModule, FormsModule, ButtonsModule, InputsModule, DropDownsModule, IconsModule]
})
export class DrawingNumberRequestComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  @Input() rtaNum?: number;
  @Input() createdBy?: number;
  @Input() rtaNumLocked = false;
  @Input() createdByLocked = false;
  @Input() subPsl?: number = 1;
  @Input() productLine?: string;
  @Input() partId?: string;
  @Input() partNumber?: string;

  @Output() created = new EventEmitter<DrawingNumberResult>();

  pnaParts: PnaPart[] = [];
  groupedPnaParts: GroupResult[] = [];
  selectedPnaPartId?: number;
  description = '';
  localRtaNum?: number;
  localCreatedBy?: number;
  isSubmitting = false;
  errorMessage = '';
  lastResult?: DrawingNumberResult;
  showAllPnaParts = false;
  warningMessage = '';

  get canSubmit(): boolean {
    return !!this.selectedPnaPartId && this.description.trim().length > 0;
  }

  ngOnInit(): void {
    this.localRtaNum = this.rtaNum;
    this.localCreatedBy = this.createdBy;
    this.loadPnaParts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rtaNum'] && !changes['rtaNum'].firstChange) {
      this.localRtaNum = this.rtaNum;
    }
    if (changes['createdBy'] && !changes['createdBy'].firstChange) {
      this.localCreatedBy = this.createdBy;
    }
    if (changes['subPsl'] && !changes['subPsl'].firstChange) {
      this.loadPnaParts();
    }
    if (changes['productLine'] && !changes['productLine'].firstChange) {
      this.loadPnaParts();
    }
  }

  private loadPnaParts(): void {
    this.api.getPnaParts(this.subPsl, this.showAllPnaParts).subscribe({
      next: parts => {
        this.pnaParts = parts.map(part => ({
          ...part,
          pnaGenre: part.pnaGenre || { id: part.pnaGenreId, name: 'Other' }
        }));
        const withGenreName = this.pnaParts.map(part => ({
          ...part,
          pnaGenreName: part.pnaGenre?.name || 'Other'
        }));

        // Group items by pnaGenreName
        const groupMap = new Map<string, typeof withGenreName>();
        for (const item of withGenreName) {
          const key = item.pnaGenreName;
          if (!groupMap.has(key)) {
            groupMap.set(key, []);
          }
          groupMap.get(key)!.push(item);
        }

        // Sort items within each group by sequence then name
        for (const items of groupMap.values()) {
          items.sort((left, right) => {
            const leftSequence = left.sequence ?? Number.MAX_SAFE_INTEGER;
            const rightSequence = right.sequence ?? Number.MAX_SAFE_INTEGER;
            if (leftSequence !== rightSequence) return leftSequence - rightSequence;
            return (left.name || '').localeCompare(right.name || '');
          });
        }

        // Sort group keys: productLine match first, then alphabetical
        const sortedKeys = Array.from(groupMap.keys()).sort((a, b) => {
          if (this.productLine) {
            const aMatch = a.toLowerCase() === this.productLine!.toLowerCase();
            const bMatch = b.toLowerCase() === this.productLine!.toLowerCase();
            if (aMatch && !bMatch) return -1;
            if (!aMatch && bMatch) return 1;
          }
          return a.localeCompare(b);
        });

        // Build GroupResult array manually to preserve order
        this.groupedPnaParts = sortedKeys.map(key => ({
          field: 'pnaGenreName',
          value: key,
          items: groupMap.get(key)!,
          aggregates: {}
        })) as GroupResult[];
      },
      error: err => { this.errorMessage = err?.message || 'Failed to load PNA parts'; }
    });
  }

  onFilterChanged(): void {
    this.loadPnaParts();
  }

  onPnaPartChange(partId: number): void {
    const part = this.pnaParts.find(p => p.id === partId);
    if (part && part.description) {
      this.description = part.description;
    }
  }

  submit(): void {
    if (!this.canSubmit) return;

    this.isSubmitting = true;
    this.errorMessage = '';
    this.warningMessage = '';

    // Warn user if no part info is provided
    if (!this.partId && !this.partNumber) {
      this.warningMessage = 'No part number or part ID provided. The drawing number will be created without a linked document.';
    }

    const request: CreateDrawingLogRequest = {
      rtaNum: this.localRtaNum,
      pnaPartId: this.selectedPnaPartId,
      description: this.description.trim(),
      createdBy: this.localCreatedBy,
      partId: this.partId,
      partNumber: this.partNumber
    };

    this.api.createDrawingLog(request).subscribe({
      next: result => {
        this.lastResult = result;
        if (result.warning) {
          this.warningMessage = result.warning;
        }
        this.created.emit(result);
        this.isSubmitting = false;
      },
      error: err => {
        this.errorMessage = err?.error?.error || err?.message || 'Failed to create drawing number';
        this.isSubmitting = false;
      }
    });
  }

  getHighlightedDescription(): string {
    const escaped = this.escapeHtml(this.description);
    const tokenRegex = /#\S+#|#\S+/g;
    return escaped.replace(tokenRegex, match => `<span class="token">${match}</span>`);
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
